import { Options as HighchartsOptions, Point, SeriesColumnOptions } from 'highcharts';
import { TFunction } from 'i18next';

import { STATIC_ELEMENT_HEIGHT, STATIC_POINT_HEIGHT, TOOLTIP_PADDING_SIZE } from '../../constants';
import { RadioInputOption } from '../../types';
import { AXIS_LINE_COLOR, DEFAULT_FONT_SIZE } from '../ForecastingStudio/config';
import { CHART_BOTTOM_MARGIN, MIN_1, MIN_15, ONE_HOUR } from '../Scheduling/config';
import { AdherenceHistoryRadioInputs, GanttChartSeries } from './types';

type CommonSeriesConfig = {
  id: string;
  nameKey: string;
};

type BarChartSeriesConfig = CommonSeriesConfig & {
  color: string;
};

type GanttChartSeriesConfig = CommonSeriesConfig & {
  color: string[];
  id: GanttChartSeries;
};

export const TRANSITION_TIME_DELAY = 400;

export const TOOLTIP_X_AXIS_THRESHOLD = 3;
const TOOLTIP_Y_AXIS_THRESHOLD = 5;

export const yAxisTicks = [0, 50, 100];

export const barChartSeriesConfig: BarChartSeriesConfig[] = [
  {
    color: '#81348D',
    id: 'in-adherence',
    nameKey: 'scheduleAdherence.inAdherence',
  },
  {
    color: '#D78FE2',
    id: 'conformance',
    nameKey: 'scheduleAdherence.conformance',
  },
];

export const ganttChartSeriesConfig: GanttChartSeriesConfig[] = [
  {
    color: ['#FF5353', '#720000'],
    id: 'outOfAdherence',
    nameKey: 'scheduleAdherence.ooaEvents',
  },
  {
    color: [], // Real color will be defined by schedule elements
    id: 'scheduled',
    nameKey: 'scheduleAdherence.scheduled',
  },
  {
    color: ['#B6E6F7', '#396A7C'],
    id: 'reality',
    nameKey: 'scheduleAdherence.reality',
  },
];

export const staticBarChartOptions: HighchartsOptions = {
  boost: {
    enabled: true,
    useGPUTranslations: true,
  },
  credits: {
    enabled: false,
  },
  chart: {
    height: 230,
    spacingLeft: 0,
    spacingRight: 0,
    style: {
      fontFamily: 'Roboto, sans-serif',
    },
    type: 'column',
  },
  legend: {
    enabled: false,
  },
  plotOptions: {
    column: {
      borderRadius: 2,
      borderWidth: 0,
    },
    series: {
      /**
       * Use Infinity to enable chart to use animation when series of multiple queues are visible
       * see https://api.highcharts.com/highcharts/plotOptions.series.animationLimit
       */
      animationLimit: Number.POSITIVE_INFINITY,
      cursor: 'pointer',
      point: {},
    },
  },
  series: barChartSeriesConfig.map(({ color, id }) => ({
    color,
    id,
    states: {
      hover: {
        enabled: true,
      },
    },
    type: 'column',
  })) as SeriesColumnOptions[],
  title: {
    text: '',
  },
  tooltip: {
    backgroundColor: 'transparent',
    borderColor: 'transparent',
    outside: true,
    style: { width: 2000 }, // Prevent cropping when chart contains very few values (not being full-size rendered)

    positioner(labelWidth, labelHeight, point) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const { chart } = this; // `this` is of type `TooltipOptions`
      const {
        pointer: {
          chartPosition: { left, top },
        },
        plotLeft,
      } = chart;

      const points = chart.hoverPoints as Point[];
      const pointWithHighestValue = points.reduce((highestPoint, currentPoint) => {
        if (currentPoint.y && highestPoint.y && currentPoint.y > highestPoint.y) {
          return currentPoint;
        }
        return highestPoint;
      });

      const pointWidth = chart.series[0].barW;

      let x = Math.floor(left + plotLeft + point.plotX - labelWidth / 2 + pointWidth / 2 - TOOLTIP_X_AXIS_THRESHOLD);

      if (x < 0) {
        x = 0;
      } else if (x + labelWidth + TOOLTIP_PADDING_SIZE > window.innerWidth) {
        x = window.innerWidth - labelWidth - TOOLTIP_PADDING_SIZE;
      }

      return {
        x,
        // @ts-expect-error: incompatible type 😥
        y: Math.max(top + pointWithHighestValue.plotY - labelHeight - TOOLTIP_Y_AXIS_THRESHOLD, 0),
      };
    },
    shadow: false,
    shared: true,
    useHTML: true,
  },
  xAxis: {
    currentDateIndicator: false,
    showEmpty: false,
    labels: {
      style: {
        fontSize: DEFAULT_FONT_SIZE,
      },
      useHTML: true,
      y: 30,
    },
    lineColor: '#e6e6e6',
    tickColor: AXIS_LINE_COLOR,
    title: {
      text: '',
    },
    type: 'datetime',
  },
  yAxis: {
    grid: {
      enabled: false,
    },
    labels: {
      enabled: false,
    },
    showEmpty: false,
    tickAmount: 3,
    tickPositions: yAxisTicks,
    title: { text: '' },
  },
};

// Adherence History Filtering in Sidebar configuration

export const ADHERENCE_HISTORY_RANGE_FILTERS_CONSTRAINS = {
  adherenceEventsRange: {
    default: undefined,
    min: 0,
    max: 100_000,
  },
  conformanceRange: {
    default: undefined,
    min: 0,
    max: 300,
  },
  inAdherenceRange: {
    default: undefined,
    min: 0,
    max: 100,
  },
};

export const radioFilterTileOptionsObj = (
  t: TFunction
): Record<keyof AdherenceHistoryRadioInputs, RadioInputOption[]> => ({
  lateArrivals: [
    {
      value: 'true',
      label: t('scheduleAdherence.withLateArrivals'),
    },
    {
      value: 'false',
      label: t('scheduleAdherence.withoutLateArrivals'),
    },
  ],
  earlyLeaves: [
    {
      value: 'true',
      label: t('scheduleAdherence.withEarlyLeaves'),
    },
    {
      value: 'false',
      label: t('scheduleAdherence.withoutEarlyLeaves'),
    },
  ],
  includeInactiveAgents: [
    {
      value: 'true',
      label: t('scheduleAdherence.includeInactivePersons'),
    },
  ],
});

export const ganttChartOptions: HighchartsOptions = {
  credits: {
    enabled: false,
  },
  exporting: {
    enabled: false,
  },
  chart: {
    animation: false,
    height: 240,
    marginBottom: CHART_BOTTOM_MARGIN,
    resetZoomButton: {
      position: {
        y: 5,
      },
      theme: {
        fill: '#fff',
        style: {
          fontWeight: '500',
        },
      },
    },
    spacingBottom: 0,
    spacingLeft: 0,
    spacingRight: 0,
    spacingTop: 0,
    style: {
      fontFamily: 'Roboto, sans-serif',
    },
    zoomType: 'x',
  },
  plotOptions: {
    gantt: {
      borderRadius: 2,
      pointWidth: STATIC_POINT_HEIGHT,
      minPointLength: 2,
      states: {
        inactive: {
          animation: {
            duration: 0,
          },
          opacity: 1,
        },
        hover: {
          enabled: false,
        },
        normal: {
          animation: false,
        },
      },
    },
  },
  tooltip: {
    backgroundColor: '#fff',
    borderWidth: 0,
    followPointer: true,
    outside: true,
    padding: 0,
    positioner(labelWidth, labelHeight, point) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const { chart } = this; // `this` is of type `TooltipOptions`
      const {
        hoverSeries: {
          xAxis,
          userOptions: { id },
        },
        pointer: {
          chartPosition: { left, top },
        },
        plotLeft,
      } = chart;

      const yAxis = chart.hoverPoint;
      const yOffset = id === 'outOfAdherence' ? xAxis.top + 1 : 0;

      let x = Math.floor(left + plotLeft + point.plotX - labelWidth / 2 - 2 * TOOLTIP_X_AXIS_THRESHOLD);

      if (x < 0) {
        x = 0;
      } else if (x + labelWidth + TOOLTIP_PADDING_SIZE > window.innerWidth) {
        x = window.innerWidth - labelWidth - TOOLTIP_PADDING_SIZE;
      }

      return {
        x,
        y: top + xAxis.top + yAxis.plotY - yAxis.pointWidth - labelHeight - yOffset,
      };
    },

    shadow: false,
    shape: 'square',
    style: { width: 2000 }, // Prevent cropping when chart contains very few values (not being full-size rendered)
    useHTML: true,
  },
  xAxis: [
    {
      endOnTick: true,
      className: 'zoom-xAxis-minutes',
      currentDateIndicator: false,
      grid: {
        borderColor: '#fff',
        borderWidth: 2,
      },
      id: 'x-axis-minutes',
      labels: {
        align: 'left',
        style: { color: '#3a3c3e', fontSize: '12px', fontWeight: '500' },
        x: 10,
      },
      minRange: MIN_1 * 14,
      showEmpty: false,
      startOnTick: true,
      tickInterval: MIN_15,
      type: 'datetime',
    },
    {
      className: 'zoom-xAxis-hours',
      currentDateIndicator: false,
      endOnTick: true,
      grid: {
        borderColor: '#fff',
        borderWidth: 2,
      },
      id: 'x-axis-hours',
      labels: {
        align: 'left',
        style: {
          color: '#3a3c3e',
          fontSize: '16px',
          fontWeight: '500',
        },
        x: 10,
      },
      linkedTo: 0,
      minRange: MIN_1 * 14,
      showEmpty: false,
      startOnTick: true,
      tickInterval: ONE_HOUR,
      type: 'datetime',
    },
  ],
  yAxis: [
    {
      categories: [],
      grid: {
        enabled: false,
      },
      gridLineColor: '#fff',
      gridLineWidth: 0,
      labels: {
        enabled: false,
      },
      max: 2,
      min: 0,
      staticScale: STATIC_ELEMENT_HEIGHT,
      title: {
        text: '',
      },
    },
  ],
};
